import { useEffect, useState } from 'react';
import { AlertTriangle, Bot, FlaskConical, ScanFace, ShieldCheck } from 'lucide-react';
import {
  Alert, Button, Card, Dialog, Select, StatusBadge, Textarea, cn, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { formatDateTime } from '../../../lib/format';
import { fetchPhoto } from '../../hr/components/EmployeePhoto';

/**
 * Requirement 3 on the physician's screen: the pre-sign cross-check, the
 * question to the laboratory, and the identity check against the record photo.
 *
 * The cross-check is an extra check. It never confirms that a decision is
 * right, and the copy here never says it does: "no questions" means only that
 * nothing more was found to ask.
 */

// ── Types (the API's shapes) ─────────────────────────────────────

export type CrossCheckResponse = 'REVIEWED' | 'FURTHER_INVESTIGATION' | 'LAB_VERIFICATION_REQUESTED' | 'NOT_APPLICABLE' | 'AI_INCORRECT';

export interface CrossCheckQuestion {
  id: string; origin: 'RULE' | 'AI'; kind: string; severity: 'ATTENTION' | 'HIGH';
  prompt: string; sourceRef: string; sourceLabel: string;
  response: CrossCheckResponse | null; responseNote: string | null; answeredAt: string | null; carried: boolean;
}

export interface CrossCheckRun {
  id: string; createdAt: string; proposedDecision: string; stale: boolean; labReportRevision: number | null;
  ai: { status: 'COMPLETED' | 'UNAVAILABLE'; model: string | null; answeredAt: string | null; unavailableReason: string | null; droppedUncited: number };
  unavailablePolicy: 'ALLOW_WITH_ACKNOWLEDGEMENT' | 'BLOCK';
  acknowledgement: { at: string; note: string | null } | null;
  signedReviewId: string | null;
  questions: CrossCheckQuestion[];
  unanswered: number;
}

export interface LabVerificationRow {
  id: string; resultId: string; testName: string; valueSnapshot: string | null; flagSnapshot: string | null;
  question: string; status: 'OPEN' | 'CONFIRMED' | 'AMENDED' | 'CANCELLED';
  requestedAt: string; respondedAt: string | null; responseNote: string | null; amendedResultId: string | null;
}

export const RESPONSE_LABEL: Record<CrossCheckResponse, string> = {
  REVIEWED: 'Reviewed and considered',
  FURTHER_INVESTIGATION: 'Further investigation required',
  LAB_VERIFICATION_REQUESTED: 'Lab verification / correction requested',
  NOT_APPLICABLE: 'Not applicable (give the reason)',
  AI_INCORRECT: 'The concern is incorrect (explain)',
};
const NEEDS_NOTE: CrossCheckResponse[] = ['NOT_APPLICABLE', 'AI_INCORRECT'];

/** Ready to sign, as far as the cross-check is concerned. */
export function crossCheckReady(run: CrossCheckRun | null, proposalKey: string, checkedKey: string | null): boolean {
  if (!run || run.signedReviewId || run.stale || checkedKey !== proposalKey) return false;
  if (run.unanswered > 0) return false;
  if (run.ai.status === 'UNAVAILABLE') return run.unavailablePolicy !== 'BLOCK' && !!run.acknowledgement;
  return true;
}

// ── The cross-check ─────────────────────────────────────────────

export function CrossCheckPanel({ orderId, decision, restrictionCodes, clinicalAdvice, followUpMonths, onReady }: {
  orderId: string;
  decision: string;
  restrictionCodes: string[];
  clinicalAdvice: string;
  followUpMonths?: number;
  onReady: (ready: boolean) => void;
}) {
  const [run, setRun] = useState<CrossCheckRun | null>(null);
  const [checkedKey, setCheckedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [ack, setAck] = useState('');
  const proposalKey = `${decision}|${[...restrictionCodes].sort().join(',')}`;

  // The last check on this review, so a physician returning to the page
  // finds their answers where they left them.
  useEffect(() => {
    let live = true;
    api.get<{ run: CrossCheckRun | null }>(`/physician/examinations/${orderId}/cross-check`)
      .then((r) => {
        if (!live || !r.run || r.run.signedReviewId) return;
        setRun(r.run);
        // The stored proposal names the decision; restrictions are matched by re-running.
        setCheckedKey(null);
      })
      .catch(() => undefined);
    return () => { live = false; };
  }, [orderId]);

  const ready = crossCheckReady(run, proposalKey, checkedKey);
  useEffect(() => { onReady(ready); }, [ready, onReady]);

  async function runCheck() {
    if (!decision) return;
    setBusy('run'); setError(null);
    try {
      const r = await api.post<CrossCheckRun>(`/physician/examinations/${orderId}/cross-check`, {
        decision,
        ...(restrictionCodes.length ? { restrictionCodes } : {}),
        ...(clinicalAdvice.trim() ? { clinicalAdvice: clinicalAdvice.trim() } : {}),
        ...(followUpMonths ? { followUpMonths } : {}),
      });
      setRun(r); setCheckedKey(proposalKey);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  async function answer(q: CrossCheckQuestion, response: CrossCheckResponse) {
    if (!run) return;
    const note = notes[q.id]?.trim();
    if (NEEDS_NOTE.includes(response) && (!note || note.length < 5)) {
      setError('Give a short reason first, then choose the answer.');
      return;
    }
    setBusy(q.id); setError(null);
    try {
      setRun(await api.post<CrossCheckRun>(`/physician/examinations/${orderId}/cross-check/${run.id}/questions/${q.id}`, {
        response, ...(note ? { note } : {}),
      }));
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  async function acknowledge() {
    if (!run) return;
    setBusy('ack'); setError(null);
    try {
      setRun(await api.post<CrossCheckRun>(`/physician/examinations/${orderId}/cross-check/${run.id}/acknowledge`, { note: ack.trim() }));
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  const outOfDate = run && (run.stale || checkedKey !== proposalKey);

  return (
    <section aria-labelledby="cross-check" className="rounded-input border border-neutral-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2">
        <h3 id="cross-check" className="flex items-center gap-1.5 text-small font-semibold text-neutral-900">
          <ShieldCheck aria-hidden className="h-4 w-4" /> Pre-sign cross-check
        </h3>
        {run && !outOfDate && (
          <StatusBadge tone={ready ? 'success' : 'pending'}>
            {ready ? 'Answered' : run.unanswered ? `${run.unanswered} to answer` : 'Needs acknowledgement'}
          </StatusBadge>
        )}
      </div>
      <div className="space-y-3 px-3 py-3">
        <p className="text-caption font-normal text-neutral-600">
          An extra check before you sign: rule-based checks of the released results and, where available, an AI reading for
          inconsistencies. It does not confirm that a decision is correct — the decision and the signature are yours.
        </p>

        {!decision && <p className="text-small text-neutral-500">Choose a decision first; the check reads it against the record.</p>}

        {decision && (!run || outOfDate) && (
          <div>
            {run && outOfDate && (
              <p className="mb-2 text-caption font-normal text-warning-700">
                {run.stale ? 'The evidence changed since the last check.' : 'The decision or restrictions changed since the last check.'}
                {' '}Answers already given on the same evidence carry over.
              </p>
            )}
            <Button size="sm" variant="secondary" loading={busy === 'run'} onClick={runCheck}>
              {run ? 'Check again' : 'Run the cross-check'}
            </Button>
          </div>
        )}

        {run && !outOfDate && (
          <>
            {run.ai.status === 'COMPLETED' ? (
              <p className="flex items-center gap-1.5 text-caption font-normal text-neutral-500">
                <Bot aria-hidden className="h-3.5 w-3.5" />
                AI check by {run.ai.model} at {run.ai.answeredAt ? formatDateTime(run.ai.answeredAt) : '—'}
                {run.ai.droppedUncited ? ` · ${run.ai.droppedUncited} concern${run.ai.droppedUncited === 1 ? '' : 's'} not shown because they cited nothing in the record` : ''}
              </p>
            ) : (
              <Alert tone={run.unavailablePolicy === 'BLOCK' ? 'danger' : 'warning'} title="The AI check is unavailable">
                {run.ai.unavailableReason ? `(${run.ai.unavailableReason}) ` : ''}
                {run.unavailablePolicy === 'BLOCK'
                  ? 'The platform policy is to wait for it: the review cannot be signed until the check runs. Try again shortly.'
                  : 'The rule-based checks below still apply. To sign without the AI check, record that you reviewed the case manually.'}
              </Alert>
            )}

            {run.questions.length === 0 ? (
              <p className="text-small text-neutral-700">No questions were raised. That is not a confirmation that the decision is correct.</p>
            ) : (
              <ol className="space-y-2">
                {run.questions.map((q) => (
                  <li key={q.id} className={cn('rounded-input border px-3 py-2', q.answeredAt ? 'border-neutral-200 bg-neutral-50' : q.severity === 'HIGH' ? 'border-danger-200' : 'border-warning-200')}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="text-small font-medium text-neutral-900">{q.prompt}</p>
                      <StatusBadge tone={q.origin === 'AI' ? 'info' : 'neutral'}>{q.origin === 'AI' ? 'AI concern' : 'Rule check'}</StatusBadge>
                    </div>
                    <p className="mt-0.5 text-caption font-normal text-neutral-600">
                      <span className="text-neutral-500">Source: </span>{q.sourceLabel}
                    </p>
                    {q.answeredAt ? (
                      <p className="mt-1 text-caption text-neutral-700">
                        {RESPONSE_LABEL[q.response!]}{q.responseNote ? ` — “${q.responseNote}”` : ''}
                        {q.carried ? ' · answered on an earlier check of the same evidence' : ''}
                      </p>
                    ) : (
                      <div className="mt-1.5 grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                        <Select
                          aria-label="Your answer"
                          value=""
                          disabled={busy === q.id}
                          onChange={(e) => { if (e.target.value) void answer(q, e.target.value as CrossCheckResponse); }}
                        >
                          <option value="">Choose an answer…</option>
                          {(Object.keys(RESPONSE_LABEL) as CrossCheckResponse[])
                            .filter((r) => r !== 'AI_INCORRECT' || q.origin === 'AI')
                            .map((r) => <option key={r} value={r}>{RESPONSE_LABEL[r]}</option>)}
                        </Select>
                        <input
                          className="h-10 rounded-input border border-neutral-300 px-3 text-small"
                          placeholder="Short note (required for “not applicable” / “incorrect”)"
                          aria-label="Short note"
                          value={notes[q.id] ?? ''}
                          maxLength={500}
                          onChange={(e) => setNotes((n) => ({ ...n, [q.id]: e.target.value }))}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}

            {run.ai.status === 'UNAVAILABLE' && run.unavailablePolicy !== 'BLOCK' && (
              run.acknowledgement ? (
                <p className="text-caption text-neutral-700">Manual review recorded {formatDateTime(run.acknowledgement.at)}: “{run.acknowledgement.note}”</p>
              ) : (
                <div className="space-y-1.5">
                  <label className="block">
                    <span className="text-caption text-neutral-700">Record your manual review</span>
                    <Textarea rows={2} className="mt-1" value={ack} onChange={(e) => setAck(e.target.value)}
                      placeholder="I reviewed the findings manually without the AI check." />
                  </label>
                  <Button size="sm" variant="secondary" disabled={ack.trim().length < 10} loading={busy === 'ack'} onClick={acknowledge}>
                    Record manual review
                  </Button>
                </div>
              )
            )}
            {run.ai.status === 'UNAVAILABLE' && (
              <Button size="sm" variant="ghost" loading={busy === 'run'} onClick={runCheck}>Try the AI check again</Button>
            )}
          </>
        )}
        {error && <Alert>{error}</Alert>}
      </div>
    </section>
  );
}

// ── The question to the laboratory ──────────────────────────────

const VERIFY_TONE: Record<LabVerificationRow['status'], Tone> = {
  OPEN: 'pending', CONFIRMED: 'success', AMENDED: 'info', CANCELLED: 'neutral',
};
const VERIFY_LABEL: Record<LabVerificationRow['status'], string> = {
  OPEN: 'Waiting for the laboratory', CONFIRMED: 'Value confirmed', AMENDED: 'Value amended by the laboratory', CANCELLED: 'Withdrawn',
};

export function LabVerificationPanel({ orderId, results, canAsk, onChanged }: {
  orderId: string;
  results: Array<{ id: string; testName: string; value: number | string | null; unit: string | null; flag: string }>;
  canAsk: boolean;
  onChanged: () => void;
}) {
  const list = useQuery<{ items: LabVerificationRow[] }>(
    () => api.get(`/physician/examinations/${orderId}/lab-verifications`), [orderId]);
  const [asking, setAsking] = useState(false);
  const [resultId, setResultId] = useState('');
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = list.data?.items ?? [];
  if (!canAsk && items.length === 0) return null;

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/physician/examinations/${orderId}/lab-verifications`, { resultId, question: question.trim() }, crypto.randomUUID());
      setAsking(false); setQuestion(''); setResultId('');
      list.reload(); onChanged();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  async function cancel(id: string) {
    try { await api.post(`/physician/examinations/${orderId}/lab-verifications/${id}/cancel`, {}); list.reload(); onChanged(); }
    catch (e) { setError(errorMessage(e)); }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3">
        <h3 className="flex items-center gap-1.5 text-h4 text-neutral-900"><FlaskConical aria-hidden className="h-4 w-4" /> Laboratory verification</h3>
        {canAsk && <Button size="sm" variant="secondary" onClick={() => setAsking(true)}>Ask the laboratory to verify a value</Button>}
      </div>
      <div className="px-4 py-3 text-small">
        <p className="text-caption font-normal text-neutral-600">
          A value that looks wrong is sent back to the laboratory that signed it. You never change its result: the laboratory
          confirms it, or corrects it through its own amendment. The review cannot be signed while a question is open.
        </p>
        {items.length > 0 && (
          <ul className="mt-2 space-y-2">
            {items.map((v) => (
              <li key={v.id} className="rounded-input border border-neutral-200 px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-neutral-900">{v.testName}{v.valueSnapshot ? ` · ${v.valueSnapshot}` : ''}</span>
                  <StatusBadge tone={VERIFY_TONE[v.status]}>{VERIFY_LABEL[v.status]}</StatusBadge>
                </div>
                <p className="mt-0.5 text-neutral-800">“{v.question}”</p>
                {v.responseNote && <p className="mt-0.5 text-caption text-neutral-700">Laboratory: “{v.responseNote}”{v.respondedAt ? ` · ${formatDateTime(v.respondedAt)}` : ''}</p>}
                {v.status === 'OPEN' && canAsk && <Button size="sm" variant="ghost" className="mt-1" onClick={() => cancel(v.id)}>Withdraw</Button>}
              </li>
            ))}
          </ul>
        )}
        {error && <Alert className="mt-2">{error}</Alert>}
      </div>
      {asking && (
        <Dialog title="Ask the laboratory to verify a value" onClose={() => { if (!busy) setAsking(false); }}>
          <label className="mt-2 block">
            <span className="text-caption text-neutral-700">Which value</span>
            <Select className="mt-1" value={resultId} onChange={(e) => setResultId(e.target.value)}>
              <option value="">Choose a released result…</option>
              {results.map((r) => (
                <option key={r.id} value={r.id}>{r.testName}: {r.value ?? '—'}{r.unit ? ` ${r.unit}` : ''} ({r.flag.replace(/_/g, ' ').toLowerCase()})</option>
              ))}
            </Select>
          </label>
          <label className="mt-3 block">
            <span className="text-caption text-neutral-700">What looks wrong</span>
            <Textarea rows={3} className="mt-1" value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder="Haemoglobin 21.5 is implausible beside the other counts; please re-check the sample." />
          </label>
          {error && <Alert className="mt-3">{error}</Alert>}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => setAsking(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} loading={busy} disabled={!resultId || question.trim().length < 10}>Send to the laboratory</Button>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

// ── Identity against the record photo ───────────────────────────

interface IdentityPanelData {
  hasPhoto: boolean; photoUpdatedAt: string | null; needsHumanReview: boolean; message: string;
  latest: { outcome: string; checkedAt: string; note: string | null; context: string } | null;
}

const OUTCOME_LABEL: Record<string, string> = {
  CONFIRMED: 'Identity confirmed', MISMATCH: 'Does not match — needs human review',
  UNCLEAR: 'Photo not clear enough — confirm manually', NO_PHOTO: 'No photo on the record',
};

/**
 * The record photo beside the record, for identity only. `base` is the route
 * prefix: `/physician/examinations/:id` or `/encounters/:id`.
 */
export function IdentityPanel({ base, canRecord }: { base: string; canRecord: boolean }) {
  const panel = useQuery<IdentityPanelData>(() => api.get(`${base}/identity`), [base]);
  const [src, setSrc] = useState<string | null>(null);
  const [faceVisible, setFaceVisible] = useState(true);
  const [clearEnough, setClearEnough] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = panel.data;

  useEffect(() => {
    if (!p?.hasPhoto) { setSrc(null); return; }
    let live = true; let url: string | null = null;
    fetchPhoto(`${base}/identity/photo`).then((u) => { if (live) { url = u; setSrc(u); } else URL.revokeObjectURL(u); }).catch(() => undefined);
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [base, p?.hasPhoto, p?.photoUpdatedAt]);

  async function record(outcome: 'CONFIRMED' | 'MISMATCH' | 'UNCLEAR') {
    setBusy(outcome); setError(null);
    try {
      await api.post(`${base}/identity`, { outcome, faceVisible, clearEnough, ...(note.trim() ? { note: note.trim() } : {}) });
      setNote(''); panel.reload();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  if (!p) return null;
  return (
    <Card className="p-4">
      <h3 className="flex items-center gap-1.5 text-h4 text-neutral-900"><ScanFace aria-hidden className="h-4 w-4" /> Identity check</h3>
      <div className="mt-2 flex flex-wrap items-start gap-4 max-md:flex-col">
        <div className="h-28 w-24 shrink-0 overflow-hidden rounded-input border border-neutral-200 bg-neutral-100">
          {src ? <img src={src} alt="Record photo of the employee" className="h-full w-full object-cover" />
            : <span className="flex h-full items-center justify-center px-2 text-center text-caption text-neutral-500">{p.hasPhoto ? 'Loading…' : 'No photo'}</span>}
        </div>
        <div className="min-w-0 flex-1 space-y-2 text-small">
          <p className={cn('flex items-start gap-1.5', p.needsHumanReview ? 'text-warning-700' : 'text-neutral-700')}>
            {p.needsHumanReview && <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}{p.message}
          </p>
          {p.latest && (
            <p className="text-caption font-normal text-neutral-500">
              Last: {OUTCOME_LABEL[p.latest.outcome] ?? p.latest.outcome} · {formatDateTime(p.latest.checkedAt)}
              {p.latest.context === 'VENUE' ? ' · at the venue' : ' · on review'}{p.latest.note ? ` · “${p.latest.note}”` : ''}
            </p>
          )}
          <p className="text-caption font-normal text-neutral-500">
            For identity only. The photo is never used to assess health and is never sent to the AI check. A mismatch asks for a
            person to review it; it does not stop the examination.
          </p>
          {canRecord && p.hasPhoto && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-4 text-caption text-neutral-700">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={faceVisible} onChange={(e) => setFaceVisible(e.target.checked)} className="h-4 w-4 accent-primary-600" /> Face visible</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={clearEnough} onChange={(e) => setClearEnough(e.target.checked)} className="h-4 w-4 accent-primary-600" /> Clear enough to compare</label>
              </div>
              <input className="h-10 w-full rounded-input border border-neutral-300 px-3 text-small" placeholder="Note (required for a mismatch)"
                aria-label="Identity note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" loading={busy === 'CONFIRMED'} onClick={() => record('CONFIRMED')}>Matches</Button>
                <Button size="sm" variant="secondary" loading={busy === 'UNCLEAR'} onClick={() => record('UNCLEAR')}>Can’t tell — confirm manually</Button>
                <Button size="sm" variant="secondary" loading={busy === 'MISMATCH'} disabled={note.trim().length < 5} onClick={() => record('MISMATCH')}>Doesn’t match</Button>
              </div>
            </div>
          )}
          {error && <Alert>{error}</Alert>}
        </div>
      </div>
    </Card>
  );
}
